import { useAppRouter } from '@/common/route/useAppRouter';
import { StringUtils } from '@/common/utils/stringUtils';
import { useCrudActions } from '@/common/view/useCrudActions';
import { useIAPList } from '@/modules/iap/model/list';
import { useStarterPackRewardAdd } from '@/modules/starter/pack/reward/model/add';
import { ROUTE_NAME_STARTER } from '@/modules/starter/pack/router';
import { useVModal } from '@/plugins/modal/v-modal';
import { starterPackService } from '@/services/starter';
import useVuelidate from '@vuelidate/core';
import { decimal, minValue, required } from '@vuelidate/validators';
import { onMounted, reactive, watch } from 'vue';
import { useRoute } from 'vue-router';

export function useStarterPackMixins() {
    const route = useRoute();
    const router = useAppRouter();
    const crudAction = useCrudActions();

    const { fetchIapOptions, iapOptions } = useIAPList();

    const model = reactive({
        id: StringUtils.toNumber(route.params.id),
        name: null,
        selectedIAP: null,
        packageId: null,
        price: null,
        amount: 0,
        displayRate: 0,
        gameRequired: 0,
        timeUnit: null,
        durationSeconds: null,
        image: null,
        imageFile: null,
        imageStatus: null,
        isEnabled: false,
        isDefault: false,
        canEnable: false,
        canDisable: false,
        canEdit: false,
        canDelete: false,
        rewards: []
    });

    const validations = {
        name: { required },
        price: { required, decimal, minValue: minValue(0) },
        amount: { required, decimal, minValue: minValue(0) },
        packageId: { required },
        displayRate: { required, minValue: minValue(1) },
        gameRequired: { required, minValue: minValue(1) },
        durationSeconds: { required, minValue: minValue(1) }
    };

    const v$ = useVuelidate(validations, model);

    const fetch = crudAction.executeAction({
        canExecute: () => model.id != null,
        request: () => starterPackService.get(model.id),
        onSuccess: ({ data }) => Object.assign(model, data),
        onError: () => router.push({ name: ROUTE_NAME_STARTER.PACK.INDEX })
    });

    const changeIAP = (iapId: number) => {
        iapOptions.value.forEach(item => {
            if (item.value == iapId) {
                model.packageId = item.data.packageId;
                model.price = item.data.price;
                model.amount = item.data.credit;
            }
        });
    };

    watch(() => model.selectedIAP, changeIAP);

    onMounted(async () => {
        await fetchIapOptions();
        await fetch();
    });

    return { model, v$, fetch, iapOptions };
}

export function useStarterPackRewardCreationModal() {
    const vModal = useVModal();

    const MODAL_NAME = 'create-starter-pack-reward-modal';
    const { model, v$, fetchLinkableRewards, quickCreate, rewardOptions } = useStarterPackRewardAdd();

    const beforeFetch = async () => await fetchLinkableRewards();

    const rewardActions = {
        quickCreate,
        openModal: async () => {
            v$.value.$reset();
            model.type = null;
            model.data = null;
            model.quantity = null;
            vModal.open({ name: MODAL_NAME });
        },
        closeModal: () => {
            vModal.close({ name: MODAL_NAME });
        }
    };

    const rewardCreationModal = { MODAL_NAME, v$, model, beforeFetch, rewardOptions, rewardActions };

    return rewardCreationModal;
}
